import { type FormEvent, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useStudioStore } from '@/store/studio-store'
import {
  buildShowroomStudioPrompt,
  readLandingStudioHandoff,
  readShowroomStudioHandoff,
  type ShowroomStudioHandoff,
} from '@/lib/landing-studio-handoff'
import { useLocale } from '@/pages/cogo-marketing/i18n'
import { getRenewalEntryCopy } from '@/pages/cogo-marketing/renewal-entry-copy'
import { StudioEntryShell } from './studio-entry-shell'
// Measured on the appliance (2026-08-06): the two largest buttons on the
// sign-in screen start OAuth flows that an air-gapped install cannot
// complete — the identity provider is the local cogo service. Offer them
// only where they can actually work.
import { changeStudioPassword, CogoAuthError, isCogoAuthProvider } from '@/services/auth-provider'
// The appliance's front door must not offer a page the appliance does not
// have. Measured live 2026-08-19 on https://studio-dgx-dev.cogo.xyz: this
// screen rendered "가입 없이 미리보기부터 열어 보세요", and /showroom answered
// only because the edge hands unmatched paths to an SPA — the page itself is a
// cloud marketing surface a closed-network install deliberately withholds
// (appliance-entry-route.ts, operator 2026-08-10).
import { isApplianceDistribution } from '@/services/studio-presentation-profile'

// `rotate` is reached only from a 409 on sign-in: the credential was
// correct but is the one the appliance issued at installation, and no
// session exists until it is replaced.
type AuthMode = "login" | "signup" | "rotate"

type PendingAuthHandoff =
  | { kind: 'landing'; prompt: string }
  | { kind: 'showroom'; handoff: ShowroomStudioHandoff }
  | null

const AUTH_INPUT_CLASS_NAME =
  'h-12 w-full rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-4 text-sm text-[#102033] outline-none transition placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/15'

export function AuthPage() {
  const { locale } = useLocale()
  const copy = getRenewalEntryCopy(locale).auth
  const [mode, setMode] = useState<AuthMode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  // Only used by the `rotate` step; the appliance-issued password stays in
  // `password` so the replacement request can prove it without asking for it
  // a second time.
  const [newPassword, setNewPassword] = useState('')
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [pendingHandoff] = useState<PendingAuthHandoff>(() => {
    const showroomHandoff = readShowroomStudioHandoff()
    if (showroomHandoff) {
      return {
        kind: 'showroom',
        handoff: showroomHandoff,
      }
    }

    const landingHandoff = readLandingStudioHandoff()
    return landingHandoff ? { kind: 'landing', prompt: landingHandoff.prompt } : null
  })
  const pendingPrompt = pendingHandoff
    ? pendingHandoff.kind === 'showroom'
      ? buildShowroomStudioPrompt(pendingHandoff.handoff, locale)
      : pendingHandoff.prompt
    : null

  const signIn = useStudioStore((s) => s.signIn);
  const signUp = useStudioStore((s) => s.signUp);
  const signInWithGoogle = useStudioStore((s) => s.signInWithGoogle);
  const signInWithGitHub = useStudioStore((s) => s.signInWithGitHub);

  function navigateToStudio() {
    window.history.replaceState(null, "", "/studio");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  function navigateToShowroom() {
    const destination =
      pendingHandoff?.kind === 'showroom' ? '/showroom/newsletter-membership' : '/showroom'
    window.history.pushState(null, '', destination)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (mode === "rotate") {
        if (newPassword !== newPasswordConfirm) {
          setError(copy.rotateMismatch);
          return;
        }
        const { error: rotateError } = await changeStudioPassword({
          email,
          currentPassword: password,
          newPassword,
        });
        if (rotateError) throw rotateError;
      } else if (mode === "login") {
        await signIn(email, password);
      } else {
        await signUp(email, password);
      }
      navigateToStudio();
    } catch (err) {
      // 409 is not a failure: the credential was right, but it is the one the
      // appliance issued during installation and no session exists until it is
      // replaced. Showing this in red and stopping would leave the declared
      // administrator with no way in at all.
      if (err instanceof CogoAuthError && err.code === "password_change_required") {
        setMode("rotate");
        setError(null);
        return;
      }
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function handleOAuth(provider: "google" | "github") {
    setError(null);
    try {
      await (provider === "google" ? signInWithGoogle() : signInWithGitHub());
    } catch (err) {
      setError((err as Error).message);
    }
  }

  const isLogin = mode === 'login'
  const isRotating = mode === 'rotate'
  // The appliance serves no SHOWROOM, and that is the only clause it cannot
  // honour -- signup itself it serves (identity policy on this distribution
  // is `signup: "open"`, measured live 2026-08-24). Withholding the whole
  // login-mode footer removed the only control that reaches signup mode and
  // asserted instead that accounts are admin-issued, which the running server
  // contradicts. Access is limited by the LICENSE (seats, period), never by
  // hiding the door. So on the appliance the footer keeps its signup control
  // and drops only the showroom clause.
  const withholdsShowroomEntry = isLogin && isApplianceDistribution()
  const title = isRotating ? copy.rotateTitle : isLogin ? copy.loginTitle : copy.signupTitle
  const description = isRotating
    ? copy.rotateDescription
    : isLogin
      ? copy.loginDescription
      : copy.signupDescription
  const statusLabel = pendingPrompt ? copy.pendingLabel : copy.report.kicker
  const statusDescription = pendingPrompt ? copy.pendingDescription : copy.loginDescription

  return (
    <StudioEntryShell
      testId="auth-page"
      title={title}
      description={description}
      statusLabel={statusLabel}
      statusDescription={statusDescription}
      report={copy.report}
      footer={
        <>
          {isRotating ? '' : isLogin ? copy.newQuestion : copy.existingQuestion}{' '}
          <button
            type="button"
            onClick={() => {
              if (isRotating) {
                setMode('login')
                setNewPassword('')
                setNewPasswordConfirm('')
                return
              }
              if (isLogin) {
                // Appliance: no showroom to tour, so the control does the one
                // thing this install can honour -- open signup.
                if (withholdsShowroomEntry) {
                  setMode('signup')
                  return
                }
                navigateToShowroom()
                return
              }
              setMode('login')
            }}
            className="font-semibold text-[#0369A1] underline-offset-4 hover:underline"
          >
            {isRotating
              ? copy.signIn
              : isLogin
                ? withholdsShowroomEntry
                  ? copy.createAccount
                  : copy.tryFirst
                : copy.signIn}
          </button>
          {withholdsShowroomEntry ? null : <> — {copy.accountNote}</>}
        </>
      }
    >
      <div className="space-y-5">
        {pendingPrompt && (
          <div className="rounded-md bg-zinc-50 px-4 py-3 text-left">
            <p className="text-sm font-semibold text-zinc-950">{copy.savedRequest}</p>
            <p className="mt-1 line-clamp-3 text-sm leading-6 text-zinc-600">{pendingPrompt}</p>
          </div>
        )}

        {!isCogoAuthProvider && (
          <>
        <button
          type="button"
          onClick={() => handleOAuth("google")}
          className="inline-flex min-h-12 w-full items-center justify-center rounded-lg border border-[#E2E8F0] bg-white px-4 text-sm font-semibold text-[#102033] transition hover:bg-[#F8FAFC]"
        >
          {copy.google}
        </button>

        <button
          type="button"
          onClick={() => handleOAuth("github")}
          className="inline-flex min-h-12 w-full items-center justify-center rounded-lg border border-[#E2E8F0] bg-white px-4 text-sm font-semibold text-[#102033] transition hover:bg-[#F8FAFC]"
        >
          {copy.github}
        </button>

        <div className="relative py-1">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-[#E2E8F0]" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-white px-3 text-[#94A3B8]">{copy.or}</span>
          </div>
        </div>
          </>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <label htmlFor="email" className="sr-only">
            {copy.email}
          </label>
          <input
            id="email"
            data-testid="auth-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            readOnly={isRotating}
            required
            className={AUTH_INPUT_CLASS_NAME}
            placeholder={copy.email}
          />

          <label htmlFor="password" className="sr-only">
            {copy.password}
          </label>
          <input
            id="password"
            data-testid="auth-password"
            type="password"
            autoComplete={isLogin || isRotating ? 'current-password' : 'new-password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            readOnly={isRotating}
            required
            minLength={6}
            className={AUTH_INPUT_CLASS_NAME}
            placeholder={copy.password}
          />

          {isRotating && (
            <>
              <label htmlFor="new-password" className="sr-only">
                {copy.rotateNewPassword}
              </label>
              <input
                id="new-password"
                data-testid="auth-new-password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={12}
                className={AUTH_INPUT_CLASS_NAME}
                placeholder={copy.rotateNewPassword}
              />
              <label htmlFor="new-password-confirm" className="sr-only">
                {copy.rotateConfirmPassword}
              </label>
              <input
                id="new-password-confirm"
                data-testid="auth-new-password-confirm"
                type="password"
                autoComplete="new-password"
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                required
                minLength={12}
                className={AUTH_INPUT_CLASS_NAME}
                placeholder={copy.rotateConfirmPassword}
              />
            </>
          )}

          {error && (
            <p
              role="alert"
              className="rounded-md bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="auth-submit"
            disabled={loading}
            className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#0EA5E9] px-4 text-sm font-semibold text-white transition hover:bg-[#0369A1] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {loading
              ? copy.processing
              : isRotating
                ? copy.rotateSubmit
                : isLogin
                  ? copy.continueEmail
                  : copy.createAccount}
          </button>
        </form>
      </div>
    </StudioEntryShell>
  );
}
